import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2
} from '@angular/core';
import { MatCheckbox, MatTooltip } from '@angular/material';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { AccessConfig, AccessState } from 'src/app/core/schemas/subscription-plan.schema';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { SubscriptionPlanAccessService } from 'src/app/core/services/subscription-plan-access.service';

@Directive({
  selector: '[csiSubscriptionPlanAccessControl]',
  providers: [MatTooltip]
})
export class SubscriptionPlanAccessControlDirective implements OnInit, OnDestroy {
  @Input('csiSubscriptionPlanAccessControl') key: string;
  @Input() addCustomCss: string;

  private accessConfig: AccessConfig;
  private translationSub: Subscription;

  constructor(
    public subscriptionPlanAccessService: SubscriptionPlanAccessService,
    public appStateService: AppStateService,
    private el: ElementRef,
    private renderer: Renderer2,
    private tooltip: MatTooltip,
    public translocoService: TranslocoService,
    @Optional() private matCheckbox: MatCheckbox
  ) {}

  ngOnInit() {
    this.initialize(this.subscriptionPlanAccessService.flattenedSubscriptionPlanSchema);
  }

  ngOnDestroy() {
    if (this.translationSub) {
      this.translationSub.unsubscribe();
    }
  }

  initialize(flattenedSubscriptionPlanSchema) {
    this.setAccessState(flattenedSubscriptionPlanSchema);
    this.setStyles();
  }

  setAccessState(flattenedSubscriptionPlanSchema) {
    this.accessConfig = flattenedSubscriptionPlanSchema[this.key] || {};
  }

  setStyles() {
    const defaultProMessage =
      'Interested in upgrading to Pro? Please contact our team at sales@csidryeye.com';
    const defaultAddOnMessage =
      'Interested in this feature? Please contact our team at sales@csidryeye.com';

    const defaultProLabel = 'PRO';
    const defaultAddOnLabel = 'ADD ON';
    const defaultProOrAddOnLabel = 'PRO / ADD ON';

    let upgradeProMessage = defaultProMessage;
    let upgradeAddOnMessage = defaultAddOnMessage;
    let proLabel = defaultProLabel;
    let addOnLabel = defaultAddOnLabel;
    let proOrAddOnLabel = defaultProOrAddOnLabel;

    this.translationSub = this.translocoService
      .selectTranslate([
        defaultProMessage,
        defaultAddOnMessage,
        defaultProLabel,
        defaultAddOnLabel,
        defaultProOrAddOnLabel
      ])
      .subscribe((res: any) => {
        if (Array.isArray(res)) {
          upgradeProMessage = res[0] || upgradeProMessage;
          upgradeAddOnMessage = res[1] || upgradeAddOnMessage;
          proLabel = res[2] || proLabel;
          addOnLabel = res[3] || addOnLabel;
          proOrAddOnLabel = res[4] || proOrAddOnLabel;
        } else if (typeof res === 'string') {
          upgradeProMessage = res || upgradeProMessage;
        }
      });

    const htmlEl = this.el.nativeElement;
    switch (this.accessConfig.accessState) {
      case AccessState.DISABLED:
        this.subscriptionPlanAccessService.applyDisabledStyles(htmlEl, this.renderer);
        if (this.matCheckbox) {
          this.matCheckbox.disabled = true;
        }
        break;

      case AccessState.HIDDEN:
        this.renderer.setStyle(htmlEl, 'display', 'none');
        break;

      case AccessState.PRO_LOCKED:
        this.subscriptionPlanAccessService.applyLockedState(
          this.key,
          htmlEl,
          this.renderer,
          document,
          proLabel,
          this.tooltip,
          upgradeProMessage
        );
        if (this.matCheckbox) {
          this.matCheckbox.disabled = true;
        }
        break;

      case AccessState.ADD_ON_LOCKED:
        this.subscriptionPlanAccessService.applyLockedState(
          this.key,
          htmlEl,
          this.renderer,
          document,
          addOnLabel,
          this.tooltip,
          upgradeAddOnMessage
        );
        if (this.matCheckbox) {
          this.matCheckbox.disabled = true;
        }
        break;

      case AccessState.PRO_OR_ADD_ON_LOCKED:
        this.subscriptionPlanAccessService.applyLockedState(
          this.key,
          htmlEl,
          this.renderer,
          document,
          proOrAddOnLabel,
          this.tooltip,
          upgradeAddOnMessage
        );
        if (this.matCheckbox) {
          this.matCheckbox.disabled = true;
        }
        break;

      default:
        // Default state is ENABLED
        this.subscriptionPlanAccessService.clearDisabledStyles(htmlEl, this.renderer);
        this.renderer.removeStyle(htmlEl, 'display');
        this.subscriptionPlanAccessService.removeInteractionBlocker(htmlEl, this.renderer);
        break;
    }
  }
}
